// 거래명세서 「보기 양식」 공용 로직 (2026-10-06 미리보기 A안)
// 상세(ViewTransactionStatement)와 미리보기 모달(StatementPreviewModal)이 같은 계산·같은 CSS를 쓰도록 한 곳에 모은다.
// 여기 값은 화면 표시 전용 — 저장·발행·수수료 집계에는 관여하지 않는다.

/** 부가세 안내 1줄 — 법적보안 문구 확정 전 자리만 (빈 문자열이면 양식에 표시하지 않음). 골드 P1-4 연동 */
// 법적보안 확정 A안(docs/법무검토_명세서_부가세안내_2026-10-06.md) · 대표 승인 2026-10-06. 기공물=면세이므로 "부가세 별도" 표현 금지
export const STATEMENT_VAT_NOTE = '본 명세서 금액은 공급가액 기준이며, 과세·면세 구분과 세액은 별도 발행되는 세금계산서 또는 계산서에 따릅니다.';

/** "#" 뒤의 숫자만 치아로 인식 — 규격 숫자(4.5mm 등)가 개수에 섞이는 것 방지 (작성 화면과 동일) */
export const parseToothCount = (toothInfo) => {
    if (!toothInfo) return 0;
    const hashPart = toothInfo.match(/#\s*([\d,\-~.\s]+)/);
    const source = hashPart ? hashPart[1] : toothInfo;
    const matches = source.match(/\d{1,2}/g);
    if (!matches) return 0;
    return [...new Set(matches)].length;
};

/**
 * 저장된 items를 화면용으로 정규화 — 개수(저장값 우선, 없으면 치아정보 역산)·개당가·합산가 필드를 채운다.
 * (ViewTransactionStatement fetchStatement의 기존 로직을 그대로 옮김)
 */
export const normalizeStatementItems = (items) => (items || []).map(item => {
    // 저장된 개수가 있으면(0 포함) 그대로 사용, 없을 때만 치아정보에서 계산
    const toothCount = (item.toothCount !== undefined && item.toothCount !== null)
        ? item.toothCount
        : parseToothCount(item.toothInfo);
    const unitPrice = item.unitPrice || item.pricePerTooth || (toothCount > 0 ? Math.round((item.price || item.totalPrice || 0) / toothCount) : 0);
    const totalPrice = item.totalPrice || item.price || (toothCount * unitPrice);
    return {
        ...item,
        toothCount,
        unitPrice,
        pricePerTooth: unitPrice,
        price: totalPrice,
        totalPrice: totalPrice,
    };
});

/** 소계·할인·합계·치아수 — 금값 파생 행의 개수 1은 치아가 아님(금액 합계에는 포함, 작성 화면과 동일) */
export const computeStatementTotals = (items, discount, discountType) => {
    const list = items || [];
    const subtotal = list.reduce((sum, item) => sum + (item.price || 0), 0);
    const totalTeeth = list.reduce((sum, item) => sum + (item.itemType === 'gold' ? 0 : (item.toothCount || 0)), 0);
    let discountAmount = 0;
    if (discountType === 'amount' || !discountType) {
        discountAmount = Number(discount) || 0;
    } else {
        discountAmount = Math.round(subtotal * (Number(discount) || 0) / 100);
    }
    const total = subtotal - discountAmount;
    return { subtotal, discountAmount, total, totalTeeth };
};

/** 문서 저장값으로 바로 합계 계산 (미리보기 등 편집 상태가 없는 곳) */
export const totalsOfStatement = (statement, items) =>
    computeStatementTotals(items, statement?.totalDiscount || statement?.discount || 0, statement?.discountType || 'amount');

export const formatStatementDate = (ts) => {
    if (!ts) return '-';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
};

export const formatStatementShortDate = (ts) => {
    if (!ts) return '-';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return `${d.getMonth() + 1}-${d.getDate()}`;
};

/** 최근 수정 요청(dispute) 노트 — 이력 블록·행 하이라이트용 */
export const lastDisputeNoteOf = (statement) => {
    if (!statement?.settlementNotes) return null;
    const disputes = (statement.settlementNotes || []).filter(n => n.type === 'dispute');
    return disputes.length > 0 ? disputes[disputes.length - 1] : null;
};

/** 수정 요청 줄 식별 키 (2026-10-23) — 줄 순서가 바뀌어도(정렬·추가·삭제) 같은 줄을 찾기 위해 노트에 함께 저장 */
// serviceName 추가 (2026-10-23 검토 회의) — 같은 의뢰서·같은 치식의 두 줄(예: 크라운 + 포스트)을 구분.
// 앱 줄은 name 필드라 serviceName이 없으면 name으로 대신한다
export const statementItemKeyOf = (item) => ({
    orderId: item?.orderId ?? null,
    patientName: item?.patientName || '',
    toothInfo: item?.toothInfo || '',
    serviceName: item?.serviceName || item?.name || '',
});
// b = 노트에 저장된 키. 구 노트(serviceName 키 없음)는 serviceName 비교를 건너뛴다 (기존 키로 폴백)
const sameItemKey = (a, b) => (a?.orderId ?? null) === (b?.orderId ?? null)
    && (a?.patientName || '') === (b?.patientName || '')
    && (a?.toothInfo || '') === (b?.toothInfo || '')
    && (b?.serviceName === undefined || (a?.serviceName || '') === (b?.serviceName || ''));

/**
 * dispute 노트의 checkedItems를 { index: reason } 맵으로 변환 (발급자 하이라이트·치과 재검토 미리 체크용)
 * - itemKey가 있는 노트: items에서 키가 같은 줄을 찾는다 (못 찾으면 = 그 줄이 고쳐짐 → 표시 안 함)
 * - 구 노트(itemKey 없음): 저장된 index 그대로 (폴백)
 */
export const disputedIndexMapOf = (lastDisputeNote, items) => {
    if (!lastDisputeNote?.checkedItems) return {};
    const map = {};
    const used = new Set();
    lastDisputeNote.checkedItems.forEach(ci => {
        let idx;
        if (ci.itemKey && Array.isArray(items)) {
            idx = items.findIndex((it, i) => !used.has(i) && sameItemKey(statementItemKeyOf(it), ci.itemKey));
            if (idx < 0) return;
        } else {
            idx = ci.index ?? ci.itemIndex;
        }
        if (idx == null) return;
        used.add(idx);
        map[idx] = ci.reason || '';
    });
    return map;
};

/**
 * 월 합계 기준일 (2026-10-23) — 세무 자료 엑셀(utils/excelExport.js exportTaxMonthlyReport의 monthKeyDate)과 같은 규칙:
 * 명세서 기간 종료일(endDate) → 없으면 발행일(publishedAt) → 없으면 작성일(createdAt). toDate는 호출부의 날짜 변환기
 */
export const statementMonthKeyDate = (s, toDate) => toDate(s?.endDate) || toDate(s?.publishedAt) || toDate(s?.createdAt);

/** 줄공책 스타일 — 화면/인쇄 공통 (상세·미리보기 모달이 같은 CSS를 <style>로 삽입) */
export const STATEMENT_LEDGER_CSS = `
                /* 줄공책 스타일 — 화면/인쇄 공통 */
                .ledger-table { font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif; }
                .ledger-table thead th {
                    background: #FAFAF7 !important;
                    color: #0f172a;
                    font-weight: 700;
                    border-top: 2px solid #0f172a !important;
                    border-bottom: 3px double #0f172a !important;
                    padding: 10px 8px !important;
                }
                .ledger-table tbody tr {
                    height: 36px;
                }
                .ledger-table tbody tr td {
                    border-bottom: 1px solid #CBD5E1 !important;
                    padding: 8px !important;
                }
                .ledger-table tbody tr:last-child td {
                    border-bottom: 2px solid #0f172a !important;
                }
                .ledger-area {
                    position: relative;
                    background:
                        linear-gradient(to right, #DC2626 0, #DC2626 2px, transparent 2px) no-repeat 32px 0/2px 100%;
                }

                @media print {
                    .print-only { display: inline !important; }
                    body * { visibility: hidden; }
                    .print-area, .print-area * { visibility: visible; }
                    .print-area {
                        position: absolute;
                        left: 0; top: 0;
                        width: 100%;
                        border: none !important;
                        border-radius: 0 !important;
                        padding: 24px 28px !important;
                        box-shadow: none !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    .no-print { display: none !important; }
                    .ledger-table tbody tr td { border-bottom: 1px solid #94a3b8 !important; }
                    .ledger-table thead th { border-top: 2px solid #000 !important; border-bottom: 3px double #000 !important; }
                    .ledger-table tbody tr:last-child td { border-bottom: 2px solid #000 !important; }
                }
            `;
